/**
 * TicketsList Component - Hedita Design System
 * Feature: Maintenance - Lista ticket manutenzione
 * Stile: Premium Orange Dark Theme
 */

import { useState } from "react";
import { Plus, Wrench, Pencil, Trash2, CheckCircle2, Clock, AlertTriangle, Filter } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useTicketsManutenzione, useAggiornaStatoTicket, useDeleteTicketManutenzione, useTicketsStats } from "../hooks";
import { STATO_OPTIONS, PRIORITA_CONFIG, STATO_CONFIG, type StatoTicket } from "../types";
import type { TicketManutenzione } from "../types";

const STATO_ICON: Record<StatoTicket, React.ReactNode> = {
  'Aperto': <AlertTriangle className="h-4 w-4" />,
  'In lavorazione': <Clock className="h-4 w-4" />,
  'Risolto': <CheckCircle2 className="h-4 w-4" />,
  'Chiuso': <CheckCircle2 className="h-4 w-4" />,
};

export function TicketsList() {
  const [filterStato, setFilterStato] = useState<StatoTicket | null>(null);
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  
  const { data: tickets = [], isLoading } = useTicketsManutenzione();
  const { data: stats } = useTicketsStats();
  const aggiornaStato = useAggiornaStatoTicket();
  const deleteTicket = useDeleteTicketManutenzione();

  const handleStatoChange = (id: string, stato: StatoTicket) => {
    aggiornaStato.mutate({ id, stato });
  };

  const handleDelete = (id: string) => {
    deleteTicket.mutate(id, { onSuccess: () => setConfirmDel(null) });
  };

  const filtered = filterStato ? tickets.filter(t => t.stato === filterStato) : tickets;

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-7 w-32 mb-2" />
            <Skeleton className="h-4 w-48" />
          </div>
          <Skeleton className="h-10 w-36" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-24 rounded-xl" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Skeleton className="h-48 rounded-2xl" />
          <Skeleton className="h-48 rounded-2xl" />
          <Skeleton className="h-48 rounded-2xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-foreground">Manutenzione</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Gestisci guasti ed interventi</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2.5 rounded-xl border transition-all ${
              showFilters 
                ? "border-primary bg-primary/5 text-primary" 
                : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            <Filter className="h-4 w-4" />
          </button>
          <button 
            onClick={() => window.location.href = "/team/manutenzione/nuovo"}
            className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-primary/90 transition-all shadow-sm"
          >
            <Plus className="h-4 w-4" /> Nuovo ticket
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {STATO_OPTIONS.map(s => {
          const cfg = STATO_CONFIG[s];
          const count = stats?.[s === 'Aperto' ? 'aperti' : s === 'In lavorazione' ? 'inLavorazione' : s === 'Risolto' ? 'risolti' : 'chiusi'] || 0;
          const active = filterStato === s;
          
          return (
            <button
              key={s}
              onClick={() => setFilterStato(active ? null : s)}
              className={`rounded-xl border p-4 text-center transition-all ${
                active 
                  ? `${cfg.bg} ${cfg.border} ring-1 ring-primary/20` 
                  : "border-border bg-card hover:border-primary/30"
              }`}
            >
              <div className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg mb-3 ${cfg.bg} ${cfg.color}`}>
                {STATO_ICON[s]}
                <span>{s}</span>
              </div>
              <div className={`text-3xl font-bold ${active ? cfg.color : 'text-foreground'}`}>
                {count}
              </div>
            </button>
          );
        })}
      </div>

      {/* Urgent Alert */}
      {stats?.urgenti && stats.urgenti > 0 && (
        <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-rose-500/20 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-rose-400" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-rose-400">
              {stats.urgenti} ticket {stats.urgenti === 1 ? 'urgente' : 'urgenti'} in attesa
            </p>
            <p className="text-xs text-muted-foreground">
              Richiedono attenzione immediata
            </p>
          </div>
          <button 
            onClick={() => window.location.href = "/team/manutenzione/nuovo"}
            className="px-4 py-2 rounded-lg bg-rose-500 text-white text-sm font-medium hover:bg-rose-600 transition-colors"
          >
            Gestisci
          </button>
        </div>
      )}

      {/* Filter Bar */}
      {showFilters && (
        <div className="flex flex-wrap gap-2 p-3 bg-secondary/30 rounded-xl border border-border/50">
          <button 
            onClick={() => setFilterStato(null)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
              filterStato === null 
                ? "border-primary bg-primary/10 text-primary" 
                : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            Tutti
          </button>
          {STATO_OPTIONS.map(s => (
            <button 
              key={s} 
              onClick={() => setFilterStato(filterStato === s ? null : s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all flex items-center gap-1.5 ${
                filterStato === s 
                  ? `${STATO_CONFIG[s].bg} ${STATO_CONFIG[s].color} ${STATO_CONFIG[s].border}` 
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {STATO_ICON[s]}
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Ticket Grid */}
      {filtered.length === 0 ? (
        <div className="bg-card border border-border rounded-2xl p-12 text-center">
          <div className="w-16 h-16 rounded-2xl bg-secondary/50 border border-border/50 flex items-center justify-center mx-auto mb-4">
            <Wrench className="h-8 w-8 text-muted-foreground/40" />
          </div>
          <p className="font-medium text-foreground mb-1">
            {filterStato ? `Nessun ticket ${filterStato.toLowerCase()}` : "Nessun ticket"}
          </p>
          <p className="text-sm text-muted-foreground mb-4">
            {filterStato ? "Prova a cambiare il filtro" : "Segnala un guasto per iniziare"}
          </p>
          <button 
            onClick={() => window.location.href = "/team/manutenzione/nuovo"}
            className="px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-all"
          >
            Nuovo ticket
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((t: TicketManutenzione) => {
            const stCfg = STATO_CONFIG[t.stato];
            const priCfg = PRIORITA_CONFIG[t.priorita];
            const isConfirming = confirmDel === t.id;
            
            return (
              <div key={t.id} className="bg-card border border-border rounded-2xl p-5 hover:border-primary/30 transition-all group">
                {/* Header */}
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-secondary/50 border border-border/50 flex items-center justify-center flex-shrink-0">
                      <Wrench className="h-5 w-5 text-muted-foreground/60" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-foreground text-sm leading-tight truncate">{t.titolo}</h3>
                      {t.struttura_nome && (
                        <p className="text-xs text-muted-foreground truncate">
                          {t.struttura_nome}{t.camera_nome ? ` · ${t.camera_nome}` : ""}
                        </p>
                      )}
                    </div>
                  </div>
                  <span className={`text-[11px] px-2 py-1 rounded-lg font-medium flex-shrink-0 ${stCfg.bg} ${stCfg.color} border ${stCfg.border}`}>
                    {t.stato}
                  </span>
                </div>

                {/* Meta */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className={`text-[11px] px-2 py-1 rounded-lg font-medium border ${priCfg.bg} ${priCfg.color} ${priCfg.border}`}>
                    {t.priorita}
                  </span>
                  {t.categoria && t.categoria !== "Altro" && (
                    <span className="text-[11px] px-2 py-1 rounded-lg bg-secondary/50 text-muted-foreground border border-border/50">
                      {t.categoria}
                    </span>
                  )}
                </div>

                {t.descrizione && (
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{t.descrizione}</p>
                )}

                {t.assegnato_nome && (
                  <div className="flex items-center gap-2 mb-4 p-2 bg-secondary/30 rounded-lg">
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary flex-shrink-0">
                      {t.assegnato_nome[0]}
                    </div>
                    <span className="text-xs text-muted-foreground">{t.assegnato_nome}</span>
                  </div>
                )}

                {/* Quick Status Update */}
                <div className="flex gap-1.5 mb-4">
                  {STATO_OPTIONS.slice(0, 3).map(s => (
                    <button 
                      key={s} 
                      onClick={() => handleStatoChange(t.id, s)}
                      disabled={aggiornaStato.isPending}
                      className={`flex-1 py-2 rounded-lg text-[11px] font-medium border transition-all ${
                        t.stato === s 
                          ? `${STATO_CONFIG[s].bg} ${STATO_CONFIG[s].color} ${STATO_CONFIG[s].border}` 
                          : "border-border/50 text-muted-foreground hover:border-primary/30 hover:text-foreground bg-secondary/20"
                      }`}
                    >
                      {s === 'Aperto' ? 'A' : s === 'In lavorazione' ? 'In Corso' : 'Risolto'}
                    </button>
                  ))}
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-3 border-t border-border/50">
                  {isConfirming ? (
                    <>
                      <button 
                        onClick={() => setConfirmDel(null)}
                        className="flex-1 py-2 rounded-lg border border-border text-xs text-muted-foreground hover:bg-secondary/50 transition-colors"
                      >
                        Annulla
                      </button>
                      <button 
                        onClick={() => handleDelete(t.id)}
                        disabled={deleteTicket.isPending}
                        className="flex-1 py-2 rounded-lg bg-rose-500 text-white text-xs font-medium hover:bg-rose-600 transition-colors disabled:opacity-50"
                      >
                        {deleteTicket.isPending ? "..." : "Elimina"}
                      </button>
                    </>
                  ) : (
                    <>
                      <button 
                        onClick={() => window.location.href = `/team/manutenzione/${t.id}`}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border border-border/50 text-xs text-muted-foreground hover:border-primary/50 hover:text-foreground transition-all bg-secondary/20"
                      >
                        <Pencil className="h-3.5 w-3.5" /> Modifica
                      </button>
                      <button 
                        onClick={() => setConfirmDel(t.id)}
                        className="p-2 rounded-lg border border-border/50 text-muted-foreground hover:text-rose-400 hover:border-rose-400/50 hover:bg-rose-500/10 transition-all"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

